import { FSMessage } from "src/common/keys/protos/collections_pb";
import {
    Account, DriveEntryTypes, DirectoryEntity, EntryItem, DriveEntryType, sortBy, SORT_DIRECTION, SortFieldType, parseValidDate, Directory, dayjs,
    DriveItemType, GrantSetType
} from "src/common";
import { FilenameTokenizer } from "./search-tokenizer.class";
import _ from "lodash";

export interface SearchNodeBase {
    id: string;
    collection_id: string;
    linked_collection_id: string;
    parent_id: string;
    name: string;
    type: DriveEntryTypes;
    last_modification_date: string;
    name_tokens: string[];
    sequences?: number[];
}

const tokenizer = new FilenameTokenizer();
export class SearchNode implements SearchNodeBase {
    id!: string;
    collection_id!: string;
    linked_collection_id!: string;
    parent_id!: string;
    name!: string;
    type!: DriveEntryTypes;
    last_modification_date!: string;
    name_tokens!: string[];
    constructor(search_node: SearchNodeBase) {
        this.id = search_node.id;
        this.collection_id = search_node.collection_id;
        this.linked_collection_id = search_node.linked_collection_id;
        this.parent_id = search_node.parent_id || "";
        this.name = search_node.name;
        this.name_tokens = search_node.name_tokens;
        this.last_modification_date = search_node.last_modification_date;
        this.type = search_node.type;
    }

    get item(): SearchNodeBase {
        return {
            id: this.id,
            collection_id: this.collection_id,
            linked_collection_id: this.linked_collection_id,
            parent_id: this.parent_id,
            name: this.name,
            type: this.type,
            last_modification_date: this.last_modification_date,
            name_tokens: this.name_tokens
        };
    }

    // Description: Convert snapshot directory nodes to SearchNodes
    static parseDirectoryToSearchNodes(directory: DirectoryEntity): SearchNodeMapTypes {
        // NOTE: Sort entries by date descending so we process newest first
        const entries = sortBy(directory.entries, { field: SortFieldType.lastModificationDate, direction: SORT_DIRECTION.descending }, true);
        const folder_nodes: SearchNode[] = []; // NOTE: This includes DIR and LINK nodes
        const search_nodes = _.map(entries, (entry: EntryItem) => {
            const _search_node = new SearchNode({
                id: entry.id,
                collection_id: entry.collection_id,
                linked_collection_id: entry.linked_collection_id,
                name: entry.name,
                type: entry.type,
                last_modification_date: parseValidDate(entry.lastModificationDate),
                parent_id: directory.id,
                name_tokens: tokenizer.getTokens(entry.name)
            });
            // NOTE: Parse into type arrays
            (_search_node.type === DriveEntryType.LINK || _search_node.type === DriveEntryType.DIR) && folder_nodes.push(_search_node);
            return _search_node;
        });

        return {
            folder_nodes: folder_nodes.length > 0 ? folder_nodes : undefined,
            search_nodes
        };
    }

    // Description: return a Search n ode for a Disjointed ACL Nodes to DB
    static async parseDisjointedACLNodes(collection_id: string, dir: FSMessage.Dir, current_account: Account, grant_set?: GrantSetType):
        Promise<SearchNode | undefined> {
        const decoded_name = !!grant_set ? await Directory.getDirectoryMaintainersName(dir, current_account, grant_set) : DriveItemType.Shared_Folder;
        const _date = dir.getLastUpdate();
        const latest_date = dir.hasLastLocalFsUpdate() && dayjs(dir.getLastLocalFsUpdate()).isValid() ? dayjs.utc(dir.getLastLocalFsUpdate()).local() :
            !!_date ? dayjs.unix(_date) : undefined;
        return !!decoded_name ? new SearchNode({
            id: dir.getId_asB64(), // NOTE: THIS IS THE DIRECTORY ID for root_node entry.getId_asB64() OR Helpers.b64Encode(entry.getId_asU8(), true)
            collection_id,
            linked_collection_id: "",
            name: decoded_name,
            type: DriveEntryType.DIR,
            last_modification_date: !!latest_date ? latest_date.format("MM/DD/YYYY h:mm A") : "",
            parent_id: dir.getParentId_asB64(),
            name_tokens: tokenizer.getTokens(decoded_name)
        }) : undefined;
    }

    // Description: Parse Link objects to SearchNode for Queuing 
    static parseLinksToSearchNodes(link: FSMessage.Link, collection_id: string = ""): SearchNode {
        return new SearchNode({
            id: link.getId_asB64(),
            collection_id,
            linked_collection_id: link.getLinkedCollectionId_asB64(),
            name: "",
            type: DriveEntryType.LINK,
            last_modification_date: "",
            parent_id: "",
            name_tokens: []
        });
    }
}

export interface SearchNodeMapTypes {
    folder_nodes?: SearchNode[], // NOTE: This includes DIR and LINK nodes
    search_nodes: SearchNode[]
}

export interface SearchBase {
    id: string;
    collection_id: string;
    sequence: number;
    cursor_sequence: number;
    cursor_sub_sequence: number;
    has_more: number; // Dexie does not index types: boolean (https://dexie.org/docs/Version/Version.stores()#indexable-types)
}
